// BuyInAmountBox.tsx
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { formatCurrency } from '@/pages/table/utils/utils';
import TetherBadgeViolet from '@/assets/TetherBadgeViolet.svg';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';

import styles from './BuyInAmountBox.module.css';

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60000)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor((time % 60000) / 1000)
    .toString()
    .padStart(2, '0');

  return `${minutes}:${seconds}`;
};

export const getTimerProgressColor = (initialTime: number, currentTime: number): string => {
  if (initialTime) {
    const percentage = currentTime / initialTime;
    if (percentage > 0.5) {
      return 'ph-primary-violet';
    }
    if (percentage > 0.25) {
      return 'ph-orange';
    }
    return 'ph-red';
  }
  return 'ph-primary-violet';
};

interface BuyInAmountBoxProps {
  label: string;
  amount: number;
  setAmount: (amount: number) => void;
  balance: number;
  minValue: number;
  maxValue: number;
  seatReserved?: boolean;
  initialTimer?: number;
  time?: number;
  showTimer?: boolean;
  isRebuy?: boolean;
  autoTopUp?: boolean;
}
const BuyInAmountBox: React.FC<BuyInAmountBoxProps> = ({
  label,
  amount,
  setAmount,
  balance,
  minValue,
  maxValue,
  seatReserved = false,
  initialTimer = 0,
  time = 0,
  showTimer = true,
  isRebuy,
  autoTopUp,
}) => {
  const [amountTxt, setAmountTxt] = useState<string>(amount.toFixed(2));
  const [valid, setValid] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const setMin = () => {
    if (balance < minValue) {
      setError('Not enough balance for minimum buy-in.');
      return;
    }
    setAmountTxt(minValue.toFixed(2));
    setAmount(minValue);
  };

  const setMax = () => {
    if (balance < maxValue) {
      setError('Not enough balance for maximum buy-in.');
      return;
    }
    setAmountTxt(Math.min(balance, maxValue).toFixed(2));
    setAmount(maxValue);
  };

  const setAmountBalance = () => {
    if (!balance || balance < minValue) {
      setError('Not enough balance');
      return;
    }
    setAmountTxt(Math.min(balance, maxValue).toFixed(2));
  };

  useEffect(() => {
    // on rebuy, if autotop is false then allow user to set 0 amount
    if (isRebuy && autoTopUp && amount === 0) {
      setError(null);
      setValid(true);
      return;
    }

    if (amount > balance) {
      setError('Not enough balance.');
      return;
    } else if (amount < minValue) {
      setError('Buyin too low');
      setValid(false);
      return;
    } else if (amount > maxValue) {
      setError('Buyin too high');
      setValid(false);
      return;
    }

    setError(null);
    setValid(true);
  }, [amount, minValue, maxValue, balance, isRebuy, autoTopUp]);

  const handleValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const numValue = parseFloat(value);

    if (value.split('.')[1]?.length > 2) {
      return;
    }

    setAmountTxt(parseFloat(value || '0').toString());
    setAmount(numValue || 0);
  };

  const isDisabled = amount === minValue && minValue === maxValue;

  return (
    <div
      className={twMerge(
        'modal-buy-in-amount mx-auto flex flex-col gap-3 rounded-[1.5em] px-6 py-4',
        isDisabled ? 'cursor-not-allowed opacity-40' : '',
      )}
    >
      <div className="flex flex-row">
        <div className="modal-buy-in-amount-label mr-auto text-white">{label}</div>
        <div className="ml-auto flex flex-row gap-1">
          <button
            id="buy-min-value"
            disabled={minValue === 0}
            className={twMerge(
              styles.shortCutAmountButton,
              parseFloat(amountTxt) === minValue ? styles.shortCutAmountActiveButton : '',
            )}
            onClick={setMin}
          >
            {`Min ${minValue > 0 && formatCurrency(minValue)}`}
          </button>
          <button
            id="buy-max-value"
            disabled={maxValue === 0}
            className={twMerge(
              styles.shortCutAmountButton,
              balance < maxValue && 'cursor-default !border-white !border-opacity-10',
              parseFloat(amountTxt) === maxValue ? styles.shortCutAmountActiveButton : '',
            )}
            onClick={setMax}
          >
            {`Max ${maxValue > 0 && formatCurrency(maxValue)}`}
          </button>
        </div>
      </div>
      <div className="flex flex-row">
        <div className="mr-auto flex flex-col">
          <input
            autoFocus
            value={amountTxt}
            onChange={handleValue}
            className={twMerge(
              'modal-buy-in-input',
              valid ? 'text-white' : 'text-slate-500',
              isDisabled ? 'cursor-not-allowed' : '',
            )}
            disabled={isDisabled}
            type="number"
            step={0.01}
            min={0}
          />
          <div className="mr-auto text-right text-sm font-semibold text-ph-gray-evening" onClick={setAmountBalance}>
            Bal.: {formatCurrency(balance)}
          </div>
          <div className={twMerge(!error ? 'invisible' : '', 'text-2xs text-red-500')}>{error}</div>
        </div>
        <div className="tether align-center ml-auto flex flex-row">
          <div className="flex px-2">
            <img src={TetherBadgeViolet} className="min-w-[24px]" alt="Tether" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-white">Tether</span>
            <span className="text-right text-sm font-semibold text-ph-gray-cod-light">Polygon</span>
          </div>
        </div>
      </div>

      <div className="flex justify-between rounded-[0.5em] bg-ph-violet-very-dark px-3 py-2">
        {seatReserved ? (
          <>
            <p className="text-sm text-ph-white-50">
              Your minimum buy in: <span className="text-white">{formatCurrency(minValue)}</span>
            </p>
            {showTimer && (
              <p className={twMerge('text-sm', `text-${getTimerProgressColor(initialTimer, time)}`)}>
                {formatTime(time)}
              </p>
            )}
          </>
        ) : (
          <div className="flex w-full justify-start gap-2">
            <LoadingSpinner size="4" className="h-auto w-auto" />
            <p className="flex- w-full text-sm text-ph-white-50">Reserving seat</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyInAmountBox;
